import React, { useRef, useEffect, FormEvent } from "react";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import SunEditorCore from "suneditor/src/lib/core";
import { align, list, fontSize } from 'suneditor/src/plugins'
import styles from './TextEditorElement.module.scss';
import * as Input from '../../../inputs';
import { on } from "events";


type TextEditorProps = {
    element: Input.TextEditorField;
    changed(new_value: any): void;
    on_attach_file?(file: File): void;
};

const TextEditorElement: React.FC<TextEditorProps> = ({ element, changed, on_attach_file }) => {
    const editor = useRef<SunEditorCore>();
    const fileInputRef = React.useRef(null);

    const custom_button_list = on_attach_file
        ? [
            ['fontSize'],
            ['bold', 'italic', 'underline'],
            ['indent', 'outdent'],
            ['align', 'list', 'attachCommand'],
        ]
        : [
            ['fontSize'],
            ['bold', 'italic', 'underline'],
            ['indent', 'outdent'],
            ['align', 'list'],
        ];

    interface PluginCommand {
        context: any;
        util: any;
        name: string;
        display: string;
        title?: string;
        buttonClass?: string;
        innerHTML?: string;
        add: (core: any, targetElement: any) => void;
        active: (element: any) => boolean;
        action: () => void;
    }
    
    const attach_command: PluginCommand = {
        // Ensure the object is defined
        context: null,
        util: null,
        name: 'attachCommand',
        display: 'command',
        title: 'Attach File',
        buttonClass: '',
        innerHTML: '<i class="bi bi-paperclip" style="font-size: 2rem;"></i>',
        add: (core: any, targetElement: any) => {
            const context = core.context;
            const attachTag = core.util.createElement('div');
            core.util.addClass(attachTag, '__se__attach_custom');
            context.attachCommand = {
                targetButton: targetElement,
                tag: attachTag
            };
        },
        active: (element: any) => {
            return false;
        },
        action: () => {
            if (fileInputRef.current) {
                (fileInputRef.current as HTMLInputElement).click();
            }
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.target as HTMLInputElement;
        const file = target.files?.[0];
        if (file && on_attach_file) {
            on_attach_file(file);
        }
    }

    const getSunEditorInstance = (sunEditor: SunEditorCore) => {
        editor.current = sunEditor;
    };

    useEffect(() => {
        if (editor.current) {
            editor.current.onChange = (value) => changed(value);
        }
    }, [changed]);
    

    const render_form_control = () => (
        <SunEditor  
            onChange={(value) => changed(value)}
            setContents={element.value}
            getSunEditorInstance={getSunEditorInstance}
            name={element.name || element.dom_id}
            setAllPlugins={false}
            setOptions={{
                buttonList: custom_button_list, 
                height: element.height || "15vh",
                plugins: on_attach_file
                    ? [fontSize, align, list, attach_command]
                    : [fontSize, align, list]
            }}
            disable={!!element.disabled}
            hideToolbar={!!element.hide_toolbar}
        />
    );

    const render_error = () => {
        if (!element.error) return null;
        return (<div className={'text-danger'}>{element.error}</div>);
    };

    return (
        <>
            <input 
                type="file" 
                ref={fileInputRef} 
                style={{ display: 'none' }} 
                onChange={handleFileChange} 
            />
            {render_form_control()}
            {render_error()}
            {element.bottom_info}
        </>
    )
};

export default TextEditorElement;


